import { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw, Play } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const LONG_WAIT_MS = 45000;

/** Does this device decode the stored original (H.264/AAC MP4) natively? */
export function deviceCanPlayOriginal(article) {
    if (!article?.video_orig_playable) return false;
    try { return !!document.createElement("video").canPlayType('video/mp4; codecs="avc1.64001F, mp4a.40.2"'); } catch { return false; }
}

/** Shown while a freshly received video is being transcoded: light poster + note; polls the story every 8 s and hands the
 *  ready renditions back through onReady. After 45 s (or a final failure) it turns into a clear state with «retry» and —
 *  when the original is playable on this device — «play the original». Technical details stay in the admin panel. */
export default function VideoPreparing({ article, onReady, onPlayOriginal }) {
    const { t } = useLang();
    const [long, setLong] = useState(article.video_web_status === "failed");
    const [nudging, setNudging] = useState(false);
    const started = useRef(Date.now());
    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const r = await api.get(`/public/articles/${encodeURIComponent(article.slug)}`);
                const a = r.data?.article || r.data;
                if (!alive || !a) return;
                if (a.video_web_url) return onReady(a);
                if (a.video_web_status === "failed") setLong(true);
            } catch { /* keep polling */ }
            if (alive && Date.now() - started.current > LONG_WAIT_MS) setLong(true);
        };
        const id = setInterval(tick, 8000);
        const lt = setTimeout(() => alive && setLong(true), LONG_WAIT_MS);
        return () => { alive = false; clearInterval(id); clearTimeout(lt); };
    }, [article.slug, onReady]);
    const retry = async () => {
        setNudging(true);
        try { await api.post(`/public/videos/${article.id}/nudge`); } catch { /* server decides */ }
        started.current = Date.now(); setLong(false);
        setTimeout(() => setNudging(false), 1500);
    };
    const canOriginal = deviceCanPlayOriginal(article);
    const poster = article.video_poster_url || article.cover_image;
    return (
        <div className="mx-auto rounded-lg overflow-hidden bg-[#11191c] relative flex items-center justify-center" style={{ aspectRatio: "9 / 16", maxWidth: 430 }} data-testid="video-preparing" data-state={long ? "long" : "waiting"}>
            {poster && <div aria-hidden="true" className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url("${fileUrl(poster)}")`, filter: "brightness(.45)" }} />}
            <div className="relative z-[1] text-center px-6 text-white">
                {long ? (<>
                    <p className="font-bold text-sm" data-testid="video-preparing-long-title">{t("videoPreparingLong")}</p>
                    <p className="text-xs text-gray-300 mt-2 leading-relaxed">{t("videoPreparingLongNote")}</p>
                    <div className="mt-4 flex flex-col items-center gap-2">
                        {canOriginal && (
                            <button type="button" onClick={onPlayOriginal} className="inline-flex items-center gap-2 rounded-full bg-brand-red px-4 py-2 text-sm font-bold" data-testid="video-play-original"><Play className="w-4 h-4" /> {t("videoPlayOriginal")}</button>
                        )}
                        <button type="button" onClick={retry} disabled={nudging} className="inline-flex items-center gap-2 rounded-full border border-white/40 px-4 py-2 text-sm font-bold disabled:opacity-50" data-testid="video-preparing-retry"><RotateCcw className="w-4 h-4" /> {t("retry")}</button>
                    </div>
                </>) : (<>
                    <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-brand-red" />
                    <p className="font-bold text-sm" data-testid="video-preparing-title">{t("videoPreparing")}</p>
                    <p className="text-xs text-gray-300 mt-2 leading-relaxed">{t("videoPreparingNote")}</p>
                </>)}
            </div>
        </div>
    );
}
